import { CustomView } from '../../../../services/soap/dataObjects/CustomView';
import type { ColumnDefinition, QueryParams } from '../types';

interface CustomViewColumn {
  id?: string;
  label?: string;
  type?: string;
  itemname?: string;
  isVisible?: boolean;
  isFilter?: boolean;
}

interface CustomViewDefinition {
  columns?: CustomViewColumn[];
}

/**
 * Merges base columns with Custom View columns based on FIELDS in queryParams.
 * Same logic as ServiceActivities mergeColumns, adapted for Project column structure.
 */
export function mergeColumns(
  baseColumns: ColumnDefinition[],
  customViews: CustomView[],
  queryParams: QueryParams
): ColumnDefinition[] {
  if (!baseColumns || baseColumns.length === 0) return [];

  const fields = queryParams.FIELDS?.field;
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    return baseColumns;
  }

  const baseColumnsByKey = new Map<string, ColumnDefinition>();
  baseColumns.forEach(col => baseColumnsByKey.set(col.key, col));

  const customViewLabels = new Map<string, string>();
  const customViewNames = new Map<string, string>();

  for (const view of customViews) {
    if (!view.definition) continue;
    try {
      const def: CustomViewDefinition = JSON.parse(view.definition);
      if (!def.columns) continue;
      for (const col of def.columns) {
        if (!col.itemname || !col.label) continue;
        customViewLabels.set(col.itemname, col.label);
        let effectiveName = col.itemname;
        if (col.itemname.includes('|')) {
          const [, right] = col.itemname.split('|');
          if (right?.trim()) effectiveName = right.trim();
        } else if (col.type?.includes('|')) {
          const [, rightType] = col.type.split('|');
          if (rightType?.trim()) effectiveName = rightType.trim();
        }
        customViewNames.set(col.itemname, effectiveName);
      }
    } catch {
      /* ignore */
    }
  }

  const result: ColumnDefinition[] = [];
  for (const fieldName of fields) {
    const baseCol = baseColumnsByKey.get(fieldName);
    if (baseCol) {
      result.push(baseCol);
    } else {
      const label = customViewLabels.get(fieldName) ?? fieldName;
      const name = customViewNames.get(fieldName) ?? fieldName;
      result.push({ key: name, name: label });
    }
  }
  return result;
}
